import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { cn } from '../../../../lib/utils';
import {
  borderPrimary,
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../../styling/stylingHelper';
import { ResponsiveTable, MobileCardField, MobileCardList } from '../../../shared/mobile/ResponsiveTable';
import {
  getEmailSendLog,
  getEmailSendLogFileUrl,
  type EmailSendLogRow,
  AdminRestaurant,
} from '../../../../services/admin/adminService';
import { getTableFormattedDate } from '../../../../utils/dateUtils';

const PAGE_SIZE = 25;

/**
 * The `email_type` values the server stamps today (emailSendService call sites) — the filter's
 * dropdown, not a contract. A type missing here is still shown verbatim in the table; add it
 * when a new send type ships.
 */
const EMAIL_TYPE_OPTIONS = [
  'report_weekly',
  'report_revenue',
  'report_monthly',
  'report_waste',
  'report_inventoryLeakage',
  // Pre-rename rows (the type key was 'inventory' until 2026-08-17): kept so the
  // history of those sends stays filterable.
  'report_inventory',
  'report_inventoryOrder',
  'report_inventoryManagement',
  'report_priceAlerts',
  'smart_report',
  'price_alert',
  'inventory_count_alert',
  'staff_signup',
  'staff_credentials',
  'staff_account_created',
  'demo_request',
  'signup_notification',
  'auth_signup',
  'revenue_automation_failure',
  'revenue_automation_export',
  'gmail_watch_failure',
  'custom',
  'template_product_update',
  'template_good_news',
  'template_needs_input',
  'other',
];

/** Date + time — several sends of the same type can land on one day. */
function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${getTableFormattedDate(date)} ${time}`;
}

/** Green when sent, red when the send failed. */
function StatusBadge({ status, label }: { status: EmailSendLogRow['status']; label: string }) {
  return (
    <span className={cn(
      'text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap',
      status === 'sent'
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
    )}>
      {label}
    </span>
  );
}

/**
 * Delivery progression from the Resend webhook (M80), shown under the status badge: the badge
 * says whether Resend ACCEPTED the send, this says what became of it afterwards. Steps stay grey
 * until their event lands, and hovering one shows when it happened.
 *
 * Nothing renders without a Resend message id — a failed send never got one, so no event can
 * ever arrive and a row of grey steps would read as "delivery pending" for mail that was never
 * sent. (Rows sent before the webhook existed DO carry an id and stay grey forever; that is the
 * honest answer — we never received an event for them.)
 *
 * A bounce REPLACES the trail: delivered/opened can no longer follow it, so leaving greyed steps
 * beside it would only invite a second reading. `clicked` appears only once it has happened —
 * most of our mail has nothing to click, so a permanently grey step would be noise on every row.
 */
function TrackingTrail({ row, t, className }: { row: EmailSendLogRow; t: TFunction; className?: string }) {
  if (row.status === 'failed' || !row.resendMessageId) return null;

  if (row.bouncedAt) {
    return (
      <span
        className={cn('text-xs font-medium text-red-700 dark:text-red-300', className)}
        title={`${t('adminTesting.sendHistory.trackingBounced')}: ${formatSentAt(row.bouncedAt)}`}
      >
        {t('adminTesting.sendHistory.trackingBounced')}
      </span>
    );
  }

  const steps: { key: string; label: string; at: string | null }[] = [
    { key: 'delivered', label: t('adminTesting.sendHistory.trackingDelivered'), at: row.deliveredAt },
    { key: 'opened', label: t('adminTesting.sendHistory.trackingOpened'), at: row.openedAt },
  ];
  if (row.clickedAt) {
    steps.push({ key: 'clicked', label: t('adminTesting.sendHistory.trackingClicked'), at: row.clickedAt });
  }

  return (
    <span className={cn('text-xs', className)}>
      {steps.map((step, index) => (
        <span key={step.key}>
          {index > 0 && <span className={cn(tertiaryText, 'mx-1')}>·</span>}
          <span
            className={step.at
              ? 'font-medium text-green-700 dark:text-green-300'
              : tertiaryText}
            title={step.at
              ? `${step.label}: ${formatSentAt(step.at)}`
              : t('adminTesting.sendHistory.trackingPending')}
          >
            {step.label}
          </span>
        </span>
      ))}
    </span>
  );
}

/**
 * S3 keys of the report files archived with this send. `metadata` is a loose jsonb blob, so
 * `files` can be absent, null, a non-array, or hold non-string entries on rows written before
 * archiving shipped — every one of those means "no files".
 *
 * Only `reports/`-prefixed entries count. `files` held BARE FILENAMES before archiving shipped
 * (they live under `metadata.filenames` now), and the file endpoint rejects anything outside
 * `reports/` — so without this filter every pre-deploy row would show chips that 400 on click.
 */
function fileKeysOf(row: EmailSendLogRow): string[] {
  const files = (row.metadata as { files?: unknown } | null)?.files;
  if (!Array.isArray(files)) return [];
  return files.filter((f): f is string => typeof f === 'string' && f.startsWith('reports/'));
}

/** Chip label: the filename tail of the key, falling back to the whole key if it ends in a slash. */
function fileLabel(key: string): string {
  return key.slice(key.lastIndexOf('/') + 1) || key;
}

/** One chip per archived file; clicking opens the PDF in a new tab. */
function FileChips({
  fileKeys,
  openingKey,
  onOpen,
  title,
}: {
  fileKeys: string[];
  openingKey: string | null;
  onOpen: (key: string) => void;
  title: (filename: string) => string;
}) {
  return (
    <span className="flex flex-wrap gap-1 mt-1">
      {fileKeys.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onOpen(key)}
          // All chips disable while one is resolving: handleOpenFile ignores a second
          // click anyway, so leaving the others enabled would just look broken.
          disabled={openingKey !== null}
          title={title(fileLabel(key))}
          className={cn(
            'px-2 py-0.5 rounded-full border text-xs transition-colors max-w-xs truncate',
            'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
            'hover:bg-gray-50 dark:hover:bg-gray-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            secondaryText
          )}
        >
          {fileLabel(key)}
        </button>
      ))}
    </span>
  );
}

function TestBadge({ label }: { label: string }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
      {label}
    </span>
  );
}

/**
 * A group report send covers several venues at once, so it has no restaurant of its own:
 * `restaurant_id` is NULL and the venues live in `metadata.restaurantIds`. Returns how many were
 * included, or null for an ordinary single-restaurant send. `metadata` is a loose jsonb blob, so
 * a `group: true` row without a usable array still counts as a group send — of 0 known venues.
 */
function groupVenueCount(row: EmailSendLogRow): number | null {
  const metadata = row.metadata as { group?: unknown; restaurantIds?: unknown } | null;
  if (metadata?.group !== true) return null;
  return Array.isArray(metadata.restaurantIds) ? metadata.restaurantIds.length : 0;
}

/** Stands in for the restaurant name on a group send — the row belongs to no single venue. */
function GroupBadge({ label }: { label: string }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
      {label}
    </span>
  );
}

/** Paginated history of every outbound email, newest first, with filters. */
export function SendHistoryPanel({ restaurants }: { restaurants: AdminRestaurant[] }) {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or the load
  // effect below re-runs on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  const [emailType, setEmailType] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [recipient, setRecipient] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<EmailSendLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Debounced so typing in the recipient box doesn't fire a request per keystroke.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError(false);
        const result = await getEmailSendLog({
          emailType: emailType || undefined,
          restaurantId: restaurantId || undefined,
          recipient: recipient.trim() || undefined,
          from: from || undefined,
          to: to || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setRows(result.rows);
        setTotal(result.total);
      } catch (err) {
        console.error('Failed to load email send log:', err);
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [emailType, restaurantId, recipient, from, to, offset]);

  const handleOpenFile = async (key: string) => {
    if (openingKey) return;
    // The tab is opened synchronously: once we await the signed URL the click's user activation
    // is spent and browsers block window.open. The placeholder is navigated when the URL lands,
    // and closed again if the request fails. No `noopener` here — that makes window.open return
    // null by spec, leaving no handle to navigate; the opener is nulled manually instead.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      setOpeningKey(key);
      setFileError(null);
      const url = await getEmailSendLogFileUrl(key);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open archived report file:', err);
      tab?.close();
      setFileError(t('adminTesting.sendHistory.fileError'));
    } finally {
      setOpeningKey(null);
    }
  };

  const fileChipTitle = (filename: string) =>
    t('adminTesting.sendHistory.openFileTitle', { filename });

  /** Every filter change restarts at page 1 — an offset into the old result set is meaningless. */
  const applyFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setOffset(0);
  };

  const hasFilters = emailType !== '' || restaurantId !== '' || recipient.trim() !== '' || from !== '' || to !== '';
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  const inputClass = cn(
    'w-full px-3 py-2 rounded-lg border',
    'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700',
    primaryText, smallTextSize
  );

  const headerCellClass = cn(tertiaryText, 'text-xs font-medium text-left px-3 py-2 whitespace-nowrap');
  const cellClass = cn(primaryText, smallTextSize, 'px-3 py-2 align-top');

  const clearFilters = () => {
    setEmailType('');
    setRestaurantId('');
    setRecipient('');
    setFrom('');
    setTo('');
    setOffset(0);
  };

  return (
    <div className={cn(cardClass, 'p-6')}>
      <div className="mb-4">
        <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.sendHistory.title')}</h3>
        <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
          {t('adminTesting.sendHistory.description')}
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.sendHistory.filterTypeLabel')}
          </label>
          <select
            value={emailType}
            onChange={(e) => applyFilter(setEmailType, e.target.value)}
            className={inputClass}
          >
            <option value="">{t('adminTesting.sendHistory.filterTypeAll')}</option>
            {EMAIL_TYPE_OPTIONS.map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.sendHistory.filterRestaurantLabel')}
          </label>
          <select
            value={restaurantId}
            onChange={(e) => applyFilter(setRestaurantId, e.target.value)}
            className={inputClass}
          >
            <option value="">{t('adminTesting.sendHistory.filterRestaurantAll')}</option>
            {restaurants.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.sendHistory.filterRecipientLabel')}
          </label>
          <input
            type="text"
            placeholder={t('adminTesting.sendHistory.filterRecipientPlaceholder')}
            value={recipient}
            onChange={(e) => applyFilter(setRecipient, e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.sendHistory.filterFromLabel')}
          </label>
          <input
            type="date"
            value={from}
            onChange={(e) => applyFilter(setFrom, e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.sendHistory.filterToLabel')}
          </label>
          <input
            type="date"
            value={to}
            onChange={(e) => applyFilter(setTo, e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {hasFilters && (
        <button
          onClick={clearFilters}
          className={cn(secondaryText, 'text-xs underline mb-3')}
        >
          {t('adminTesting.sendHistory.clearFilters')}
        </button>
      )}

      {fileError && (
        <p className={cn(smallTextSize, 'mb-3 text-red-600 dark:text-red-400')}>{fileError}</p>
      )}

      {loading ? (
        <p className={cn(secondaryText, smallTextSize, 'py-6 text-center')}>
          {t('adminTesting.sendHistory.loading')}
        </p>
      ) : error ? (
        <p className={cn(smallTextSize, 'py-6 text-center text-red-600 dark:text-red-400')}>
          {t('adminTesting.sendHistory.error')}
        </p>
      ) : rows.length === 0 ? (
        <p className={cn(secondaryText, smallTextSize, 'py-6 text-center')}>
          {hasFilters
            ? t('adminTesting.sendHistory.emptyFiltered')
            : t('adminTesting.sendHistory.empty')}
        </p>
      ) : (
        <>
          <ResponsiveTable
            desktop={
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={cn('border-b', borderPrimary)}>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnSentAt')}</th>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnType')}</th>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnRecipient')}</th>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnSubject')}</th>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnStatus')}</th>
                      <th className={headerCellClass}>{t('adminTesting.sendHistory.columnTriggeredBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => {
                      const fileKeys = fileKeysOf(row);
                      const venueCount = groupVenueCount(row);
                      return (
                      <tr key={row.id} className={cn('border-b', borderPrimary)}>
                        <td className={cn(cellClass, 'whitespace-nowrap')}>{formatSentAt(row.sentAt)}</td>
                        <td className={cellClass}>{row.emailType}</td>
                        <td className={cellClass}>{row.recipientEmail}</td>
                        <td className={cn(cellClass, 'max-w-xs')}>
                          {venueCount !== null && (
                            <GroupBadge label={t('adminTesting.sendHistory.groupBadge', { count: venueCount })} />
                          )}
                          <span className="block truncate" title={row.subject}>{row.subject}</span>
                          {fileKeys.length > 0 && (
                            <FileChips
                              fileKeys={fileKeys}
                              openingKey={openingKey}
                              onOpen={handleOpenFile}
                              title={fileChipTitle}
                            />
                          )}
                        </td>
                        <td className={cellClass}>
                          <span className="flex items-center gap-1.5">
                            <StatusBadge
                              status={row.status}
                              label={row.status === 'sent'
                                ? t('adminTesting.sendHistory.statusSent')
                                : t('adminTesting.sendHistory.statusFailed')}
                            />
                            {row.isTest && <TestBadge label={t('adminTesting.sendHistory.testBadge')} />}
                          </span>
                          <TrackingTrail row={row} t={t} className="block mt-1" />
                          {row.status === 'failed' && row.error && (
                            <span className={cn(secondaryText, 'text-xs block mt-1 max-w-xs truncate')} title={row.error}>
                              {row.error}
                            </span>
                          )}
                        </td>
                        <td className={cellClass}>{row.triggeredBy}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            }
            mobile={
              <MobileCardList
                items={rows}
                keyOf={(row) => row.id}
                previewCount={rows.length}
                renderCard={(row) => {
                  const fileKeys = fileKeysOf(row);
                  const venueCount = groupVenueCount(row);
                  return (
                  <div className={cn('border rounded-xl p-3', borderPrimary)}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className={cn(primaryText, smallTextSize, semiBoldText, 'min-w-0 truncate')}>
                        {formatSentAt(row.sentAt)}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {venueCount !== null && (
                          <GroupBadge label={t('adminTesting.sendHistory.groupBadge', { count: venueCount })} />
                        )}
                        <StatusBadge
                          status={row.status}
                          label={row.status === 'sent'
                            ? t('adminTesting.sendHistory.statusSent')
                            : t('adminTesting.sendHistory.statusFailed')}
                        />
                        {row.isTest && <TestBadge label={t('adminTesting.sendHistory.testBadge')} />}
                      </span>
                    </div>
                    <TrackingTrail row={row} t={t} className="block mb-2" />
                    <div className="flex mb-2">
                      <MobileCardField
                        label={t('adminTesting.sendHistory.columnType')}
                        value={row.emailType}
                        divider
                      />
                      <MobileCardField
                        label={t('adminTesting.sendHistory.columnTriggeredBy')}
                        value={row.triggeredBy}
                      />
                    </div>
                    <div className="mb-2">
                      <MobileCardField
                        label={t('adminTesting.sendHistory.columnRecipient')}
                        value={row.recipientEmail}
                      />
                    </div>
                    <MobileCardField
                      label={t('adminTesting.sendHistory.columnSubject')}
                      value={row.subject}
                    />
                    {fileKeys.length > 0 && (
                      <FileChips
                        fileKeys={fileKeys}
                        openingKey={openingKey}
                        onOpen={handleOpenFile}
                        title={fileChipTitle}
                      />
                    )}
                    {row.status === 'failed' && row.error && (
                      <p className={cn(secondaryText, 'text-xs mt-2 break-words')}>{row.error}</p>
                    )}
                  </div>
                  );
                }}
              />
            }
          />

          {/* Pager — shared by both renderings */}
          <div className="flex items-center justify-between gap-3 mt-4">
            <span className={cn(secondaryText, 'text-xs')}>
              {t('adminTesting.sendHistory.pageRange', { start: pageStart, end: pageEnd, total })}
            </span>
            <span className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className={cn(
                  'px-3 py-1.5 rounded-lg transition-colors',
                  'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  primaryText, 'text-xs'
                )}
              >
                {t('adminTesting.sendHistory.previous')}
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={pageEnd >= total}
                className={cn(
                  'px-3 py-1.5 rounded-lg transition-colors',
                  'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  primaryText, 'text-xs'
                )}
              >
                {t('adminTesting.sendHistory.next')}
              </button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
